import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Insights as InsightsIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  TrendingUp as StrategyIcon,
  AccountBalance as FinanceIcon,
} from '@mui/icons-material';
import { apiClient } from '../api/client';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  backend?: string;
}

// Conversation starters tuned to the efai-strategist model's strengths.
const SUGGESTIONS = [
  'How much cash runway should we keep before raising again?',
  'Our LTV:CAC is 2.1 with 16-month payback — what should we fix first?',
  'Should we prioritise cutting burn or raising now?',
  'How do I price a new B2B product with no pricing data yet?',
];

const DISCLAIMER =
  'AI-generated guidance for information only — not regulated financial advice. Validate material decisions with a qualified professional.';

const FinancialAdvisor: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content:
        "👋 I'm your EFAI Financial Advisor & Strategist — a private model trained on McKinsey/Forbes strategy and financial-advisory material.\n\nAsk me about cash runway, fundraising, unit economics, pricing, cost control, valuation, or business strategy. Share figures and I'll be specific.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backend, setBackend] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollToBottom, [messages]);

  // Surface which backend is serving (efai-strategist vs Gemini fallback).
  useEffect(() => {
    apiClient
      .get('/advisor/health')
      .then((r) => setBackend(r.data?.backend ?? null))
      .catch(() => setBackend(null));
  }, []);

  const send = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;

    const userMsg: Message = { role: 'user', content: message, timestamp: new Date() };
    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const res = await apiClient.post('/advisor/chat', { message, history });
      const answer: string = res.data?.answer ?? 'No response.';
      const usedBackend: string | undefined = res.data?.backend;
      if (usedBackend) setBackend(usedBackend);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: answer, timestamp: new Date(), backend: usedBackend },
      ]);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          'The advisory service is unavailable. Ensure the EFAI advisor service is running (port 8088).'
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () =>
    setMessages([messages[0]]);

  const backendLabel = backend?.startsWith('ollama')
    ? 'efai-strategist (local)'
    : backend?.startsWith('gemini')
    ? 'Gemini (fallback)'
    : 'checking…';

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsightsIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Financial Advisor & Strategist
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            color={backend?.startsWith('ollama') ? 'success' : 'default'}
            label={backendLabel}
          />
          <Tooltip title="New conversation">
            <IconButton onClick={reset} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, height: '58vh', overflowY: 'auto', mb: 2 }}>
        {messages.map((m, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 2,
              flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
            }}
          >
            <Box sx={{ mt: 0.5 }}>
              {m.role === 'user' ? <PersonIcon color="action" /> : <InsightsIcon color="primary" />}
            </Box>
            <Box
              sx={{
                bgcolor: m.role === 'user' ? 'primary.main' : 'grey.100',
                color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                px: 2,
                py: 1.25,
                borderRadius: 2,
                maxWidth: '80%',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              <Typography variant="body2">{m.content}</Typography>
            </Box>
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Analysing…</Typography>
          </Box>
        )}
        <div ref={endRef} />
      </Paper>

      {messages.length <= 1 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {SUGGESTIONS.map((s) => (
            <Chip
              key={s}
              icon={s.toLowerCase().includes('strategy') ? <StrategyIcon /> : <FinanceIcon />}
              label={s}
              variant="outlined"
              onClick={() => send(s)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask about runway, fundraising, unit economics, pricing, strategy…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={loading}
        />
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={() => send()}
          disabled={loading || !input.trim()}
        >
          Send
        </Button>
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary">
        {DISCLAIMER}
      </Typography>
    </Container>
  );
};

export default FinancialAdvisor;
